/**
 * The marks a run lays on the changes its reverts name, read once on the paragraphs as
 * they stand before any revert of the run is written, and taken off before anything the
 * run built leaves it (`ooxml/revert-target-mark-attribute`).
 *
 * WHY the change is found before anything is written
 *   A card's change id numbers the paragraph as the card was proposed against it. The first
 *   revert written in that paragraph adds a change of its own and moves every id after it,
 *   so a second revert resolved by its id afterwards named another change, or two changes
 *   read alike and neither carried the id (Sentry WORD-ADDIN-TV and WORD-ADDIN-SY,
 *   2026-10-23: §9.1(j), §10.1 and §11.2 of a DXC MRA). Found on the first read and marked
 *   on its own elements, the change is found again by its mark after any write.
 *
 * WHO lays and strips
 *   - `ProposalBuilder.applyReverts`, for one build's reverts: laid on the range as handed in,
 *     stripped when its reverts are written, whether they all were or one threw
 *   - the web's `RevertOperations`, for the model's answer: laid on the document as the model
 *     was shown it, stripped before the document is read again, the same way
 *   - the add-in's Apply All fold, for a group's cards: laid on the page as first read, kept
 *     through every card's build, stripped from every package the run hands Word
 */
import type { OoxmlRange } from "./ooxml-range";
import { addRevertTargetName, REVERT_TARGET_MARK_NAMESPACE, takeRevertTargetNames } from "./ooxml/revert-target-mark-attribute";
import { Revision } from "./ooxml/revision";
import { RedlineEngine, RevertChangeReask, type RevertMarkOfTheRun, type RevertTargetsOfTheRun } from "./redline-engine";
import type { RedlineTextRevert } from "./wire/redline-payload";
import { XmlHost } from "./xml-host";

/** A revert as a run names it: the paragraph's bookmark, the reader's id, and the change as read. */
export type NamedRevert = Pick<RedlineTextRevert, "bookmark" | "changeId" | "expectedChange">;

/** What one call of `RevertTargetMarks.layOn` laid. */
export interface LaidRevertTargets {
    targetsOfTheRun: RevertTargetsOfTheRun;
    /** The names this call put on the range, which its caller takes off again once its reverts are written. */
    revertNamesLaidHere: ReadonlySet<string>;
}

export class RevertTargetMarks {
    /**
     * A revert's name: everything the revert says of its change, so two cards naming the same
     * change by the same id and reading share one name and find the one change, and the name is
     * one word of the mark (`encodeURIComponent` leaves no space in it).
     *
     * EXAMPLE
     *   _general_package_cookie, c2, DXC Legal's insertion "knowingly "
     *   -> encodeURIComponent('["_general_package_cookie","c2","ins","dxc legal","knowingly "]')
     */
    static nameOf(revert: NamedRevert): string {
        const { kind, author, text } = revert.expectedChange;
        return encodeURIComponent(JSON.stringify([revert.bookmark, revert.changeId, kind, Revision.authorKey(author), text]));
    }

    /** The mark the engine resolves one revert of the run by (`RevertChangeRequest.markOfTheRun`). */
    static markOf(revert: NamedRevert, targetsOfTheRun: RevertTargetsOfTheRun): RevertMarkOfTheRun {
        return { revertName: RevertTargetMarks.nameOf(revert), targetsOfTheRun };
    }

    /**
     * Finds each revert's change on the range as it stands, by the change as read and its id
     * (`RedlineEngine.changesARevertNamesOn`), and marks the change's own revision elements
     * with the revert's name.
     *
     * CASES, for one revert
     *   - its name was met earlier in the list            -> nothing more: the two share the one change
     *   - its paragraph is not in the range               -> nothing: the build skips such a revert
     *   - its change is found                             -> marked, the name in `markedRevertNames`
     *   - it is not (gone, read differently, two alike)   -> unmarked, the re-ask in `unresolvedRevertReasks`:
     *                                                        it fails with that reason when written
     */
    static layOn(range: OoxmlRange, reverts: readonly NamedRevert[]): LaidRevertTargets {
        const markedRevertNames = new Set<string>();
        const unresolvedRevertReasks = new Map<string, RevertChangeReask>();
        const paragraphsByBookmarkName = range.paragraphElementsByBookmarkName();
        for (const revert of reverts) {
            const revertName = RevertTargetMarks.nameOf(revert);
            /** WHEN an earlier revert of the list has this name CONSEQUENCE the two share its change, already read. */
            if (markedRevertNames.has(revertName) || unresolvedRevertReasks.has(revertName)) continue;
            const paragraph = paragraphsByBookmarkName.get(revert.bookmark);
            /** WHEN the range does not hold the revert's paragraph CONSEQUENCE nothing to read: the build skips the revert. */
            if (paragraph === undefined) continue;
            try {
                for (const change of RedlineEngine.changesARevertNamesOn(paragraph, revert)) {
                    for (const piece of change.pieces) {
                        /**
                         * CASES
                         *   - an insertion -> the insertion element the piece sits in
                         *   - a deletion   -> the deletion element
                         */
                        const revisionElement = change.kind === "ins" ? piece.insertionContainer : piece.deletionContainer;
                        if (revisionElement !== null) addRevertTargetName(revisionElement, revertName);
                    }
                }
                markedRevertNames.add(revertName);
            } catch (error) {
                /** WHEN the read failed for another reason than the change not being found CONSEQUENCE it is the caller's failure, not this revert's. */
                if (!(error instanceof RevertChangeReask)) throw error;
                unresolvedRevertReasks.set(revertName, error);
            }
        }
        return { targetsOfTheRun: { markedRevertNames, unresolvedRevertReasks }, revertNamesLaidHere: markedRevertNames };
    }

    /** Takes the given names off every paragraph of the range, leaving any other run's names where they are. */
    static stripFrom(range: OoxmlRange, revertNames: ReadonlySet<string>): void {
        /** WHEN the call laid nothing CONSEQUENCE nothing to take off. */
        if (revertNames.size === 0) return;
        for (let paragraphIndex = 0; paragraphIndex < range.paragraphCount; paragraphIndex++) takeRevertTargetNames(range.paragraphElement(paragraphIndex), revertNames);
    }

    /**
     * The package with every mark and every declaration of the mark's namespace taken off: what
     * a run that laid marks hands Word.
     *
     * CASES
     *   - the package never mentions the namespace -> as it is, unparsed
     *   - it does                                  -> parsed, stripped, and written again
     */
    static strippedFrom(packageOoxml: string): string {
        /** WHEN the package never mentions the mark's namespace CONSEQUENCE it carries no mark: handed back unparsed. */
        if (!packageOoxml.includes(REVERT_TARGET_MARK_NAMESPACE)) return packageOoxml;
        const packageDocument = XmlHost.domParser().parseFromString(packageOoxml, "application/xml");
        takeRevertTargetNames(packageDocument.documentElement, null);
        return XmlHost.xmlSerializer().serializeToString(packageDocument);
    }
}
